import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from 'src/contexts/AuthContext';
import { LinkMenuItem } from 'src/components/LinkMenuItem';
import { CompanySwitcher } from 'src/components/CompanySwitcher';
import { getCurrentCompanies, getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import { StyledHeader } from './styles';
import { getAwayRecurringMessagesCampaign } from 'src/modules/campaigns/slices';
import { useUserCompaniesService } from 'src/modules/companies/services';
import { useHandleSelectedCompany } from 'src/hooks/useHandleSelectedCompany';
import { useNavigateWithCompany } from 'src/hooks/useNavigateWithCompany';
import { generateUrlWithCompany } from 'src/helpers/helpers';
import { handleLogout } from 'src/helpers/helpers';
import { isBhiAdmin } from 'src/helpers/permissions';
import { useCurrentCompany } from 'src/modules/companies/hooks';

function Header() {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const authContext = useContext(AuthContext);
  const companyId = useSelector(getCurrentCompanyId);
  const [searchParams] = useSearchParams();
  const urlCompanyId = searchParams.get('companyId');
  const selectedCompanyId =
    urlCompanyId || (localStorage.getItem('bhiCurrentCompany') ?? companyId);
  const awayRecurringMessageCampaign = useSelector(getAwayRecurringMessagesCampaign);
  const { profile } = useSelector(selectProfile);
  const companies = useSelector(getCurrentCompanies);
  const { getCompanies } = useUserCompaniesService();
  const navigateWithCompany = useNavigateWithCompany();
  const dispatch = useDispatch();
  const { switchCompany } = useHandleSelectedCompany();
  useCurrentCompany();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const getSandboxMode = () => {
    return localStorage.getItem('bhiSandboxMode') === 'true';
  };

  useEffect(() => {
    if (!awayRecurringMessageCampaign) {
      return;
    }

    if (!selectedCompanyId) {
      return;
    }

    if (companies.length === 0) {
      getCompanies();
    }

    const interval = setInterval(async () => {
      await getCompanies();
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [awayRecurringMessageCampaign]);

  return (
    <StyledHeader>
      {selectedCompanyId && (
        <CompanySwitcher selectedCompanyId={selectedCompanyId} onSwitch={switchCompany} />
      )}
      {getSandboxMode() && (
        <Box
          sx={{
            backgroundColor: 'warning.lighter',
            padding: '8px 16px',
            borderRadius: '0px 0px 16px 16px',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            alignSelf: 'flex-start',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'fontWeightBold',
              textAlign: 'center',
            }}
          >
            Playground
          </Typography>
        </Box>
      )}
      <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
        <AccountCircleIcon sx={{ fontSize: 40 }} />
      </IconButton>
      <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <LinkMenuItem
          key={'profile'}
          text={'My Profile'}
          to={generateUrlWithCompany('/profile', selectedCompanyId)}
          onClick={() => navigateWithCompany('/profile')}
        />
        {isBhiAdmin(profile) && (
          <LinkMenuItem
            key={'companies'}
            text={'Admin'}
            to={generateUrlWithCompany('/admin/companies', selectedCompanyId)}
            onClick={() => navigateWithCompany('/admin/companies')}
          />
        )}
        <MenuItem key={'logout'} onClick={() => handleLogout(authContext, dispatch)}>
          <Typography textAlign="center">{'Log out'}</Typography>
        </MenuItem>
      </Menu>
    </StyledHeader>
  );
}

export default Header;
